// App actions the voice agents can trigger: open a tool/screen, hand the call to another deity,
// open the Council room. Module-level emitters (like openTalk) so the live-voice hook can fire
// them without importing UI; OlmpsCore / V10Home / TalkHost / CouncilHost subscribe.
import { SEAT_BY_ID } from '../v10/roster';
import type { Sector, Stage } from '../domain/olmps';

type Fn<T> = (v: T) => void;
function emitter<T>() {
  const subs = new Set<Fn<T>>();
  return {
    emit: (v: T) => subs.forEach((fn) => fn(v)),
    on: (fn: Fn<T>) => {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
    count: () => subs.size,
  };
}

// ---- seats ---------------------------------------------------------------------
const SPOKEN_SEAT: Record<string, string> = {
  atlas: 'telos',
  telos: 'telos',
  jnus: 'janus',
  astrs: 'astra',
  chronos: 'krnos',
  kronos: 'krnos',
  kairos: 'kyrs',
  hermes: 'hrmez',
  techne: 'tkne',
  moira: 'moyri',
  cleo: 'clio',
};

/** 'atlas' / 'ATLAS' / 'Kronos' / 'syqi' -> { id: seat id or 'syqi', name } (undefined when unknown). */
export function resolveSeat(raw: unknown): { id: string; name: string } | undefined {
  const k = String(raw ?? '').trim().toLowerCase().replace(/[^a-z]/g, '');
  if (!k) return undefined;
  if (k === 'syqi' || k === 'sickie' || k === 'psyche') return { id: 'syqi', name: 'SyQi' };
  const id = SPOKEN_SEAT[k] ?? k;
  const seat = SEAT_BY_ID[id];
  return seat ? { id: seat.id, name: seat.name } : undefined;
}

// ---- open a tool / screen --------------------------------------------------------
let pendingPantheon = false;
/** V10Home: true once if a Pantheon request is waiting for the canvas to pan there. */
export function takePendingPantheon(): boolean {
  const v = pendingPantheon;
  pendingPantheon = false;
  return v;
}

export type ScreenTarget = { kind: 'tool'; toolId: string } | { kind: 'home' } | { kind: 'pantheon' } | { kind: 'settings' };
const screenBus = emitter<ScreenTarget>();
export const onOpenScreen = screenBus.on;

const TOOL_TARGETS: Record<string, string> = {
  atlas: 'atlas',
  telos: 'atlas',
  oracle: 'atlas',
  kyrs: 'kyrs',
  tasks: 'kyrs',
  krnos: 'krnos',
  calendar: 'krnos',
  metis: 'metis',
  agenda: 'metis',
  clio: 'clio',
  journal: 'clio',
  tkne: 'tkne',
  projects: 'tkne',
  numa: 'numa',
  poros: 'poros',
  moyri: 'moyri',
  hrmez: 'hrmez',
  themis: 'themis',
  janus: 'jnus',
  jnus: 'jnus',
  argos: 'argos',
  nemos: 'nemos',
  astra: 'astrs',
  astrs: 'astrs',
  epic: 'epic',
  sofia: 'sofia',
};

/** Resolve a spoken target and open it. Returns the label it opened, or null when unknown. */
export function openScreen(raw: unknown): string | null {
  const k = String(raw ?? '').trim().toLowerCase().replace(/[^a-z]/g, '');
  const spoken = SPOKEN_SEAT[k];
  const toolId = TOOL_TARGETS[k] ?? (spoken ? TOOL_TARGETS[spoken] : undefined);
  if (toolId) {
    screenBus.emit({ kind: 'tool', toolId });
    return SEAT_BY_ID[toolId]?.name ?? toolId.toUpperCase();
  }
  if (k === 'home' || k === 'compass' || k === 'now') return screenBus.emit({ kind: 'home' }), 'home';
  if (k === 'pantheon' || k === 'self' || k === 'deities') {
    pendingPantheon = true; // V10Home may only mount after the shell returns home
    screenBus.emit({ kind: 'pantheon' });
    return 'the Pantheon';
  }
  if (k === 'settings') return screenBus.emit({ kind: 'settings' }), 'Settings';
  if (k === 'council') return openCouncil({}), 'the Council';
  return null;
}

// ---- open the home's live orb from elsewhere ---------------------------------------
/** A compass outside the home (TKNE's Compass view) picked a node: the app has
 *  ONE orb — the home's NOW orb — so go home and open it on that sector/stage. */
let pendingOrb: { sector: Sector | null; stage: Stage } | null = null;
export function openHomeOrb(target: { sector: Sector | null; stage: Stage }): void {
  pendingOrb = target;
  screenBus.emit({ kind: 'home' });
}
/** V10Home: the waiting orb request, once. */
export function takePendingOrb(): { sector: Sector | null; stage: Stage } | null {
  const v = pendingOrb;
  pendingOrb = null;
  return v;
}

// ---- hand the call to another deity ----------------------------------------------
export type HandOff = { id: string; name: string; from: string };
const handOffBus = emitter<HandOff>();
export const onHandOff = handOffBus.on;
export const requestHandOff = (h: HandOff) => handOffBus.emit(h);

// ---- Council ----------------------------------------------------------------------
export type CouncilRequest = { members?: string[]; topic?: string } | null;
const councilBus = emitter<CouncilRequest>();
let councilState: CouncilRequest = null;
export const onCouncil = councilBus.on;
export const getCouncilRequest = () => councilState;
/** Council lives in the Pantheon (SELF): go there, and SelfView turns its dial to COUNCIL. */
export function openCouncil(req: Exclude<CouncilRequest, null>): void {
  councilState = req;
  pendingPantheon = true;
  screenBus.emit({ kind: 'pantheon' });
  councilBus.emit(req);
}
export function closeCouncil(): void {
  councilState = null;
  councilBus.emit(null);
}
